import { Alert, Button, ButtonGroup, Flex, HStack, Spacer, Text, useBreakpointValue, VStack } from "@chakra-ui/react"
import {
    ContractReadErrorParams,
    NotEnoughFreeCollateralError,
    OverPriceLimitAfterSwapError,
    Position,
    PositionSide,
    ReducePositionInvalidError,
    UnableToQuoteError,
} from "@perp/sdk-curie"
import Big from "big.js"
import { MotionVStack } from "component/Motion"
import { PerpNumberInputWithAddons } from "component/PerpNumberInput/PerpNumberInputWithAddons"
import { TokenIconSymbol } from "component/TokenIconSymbol"
import { BASE_ASSET_ADVANCED_PRECISION, USD_PRECISION } from "constant/number"
import { EventName } from "module/analytics/types"
import { Web3WalletContainer } from "module/wallet/container/Web3WalletContainer"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { PositionOpen } from "sdk-react/clearingHouse/usePositionOpen"
import { CreateLimitOrder } from "sdk-react/limitOrder/useCreateLimitOrder"
import { useMarket } from "sdk-react/markets/useMarket"
import { scrollbarStyle } from "theme/scrollbarStyle"
import { getFirstNonZeroLog } from "util/format"
import { CreateOrder } from "../../hook/useCreateOrder"
import { CreateOrderSlider } from "./CreateOrderSlider"
import ErrorAlert from "./ErrorAlert"
import { OrderTypePriceInput } from "./OrderTypePriceInput"
import { TradingSubmitButton } from "./TradingSubmitButton"
import { TradingSubmitLimitOrder } from "./TradingSubmitLimitOrder"
import { TradingSummary } from "./TradingSummary"

export interface TradingPanelProps {
    tickerSymbol: string
    baseAssetSymbol: string
    sideDefault?: PositionSide
    existingPosition?: Position
}

export function TradingPanel({ tickerSymbol, baseAssetSymbol, sideDefault, existingPosition }: TradingPanelProps) {
    return (
        <PositionOpen.Provider initialState={{ baseAssetSymbol, sideDefault }}>
            <CreateLimitOrder.Provider
                initialState={{
                    tickerSymbol,
                    baseAssetSymbol,
                    sideDefault,
                    existingPosition,
                }}
            >
                <CreateOrder.Provider>
                    <TradingPanelInner tickerSymbol={tickerSymbol} />
                </CreateOrder.Provider>
            </CreateLimitOrder.Provider>
        </PositionOpen.Provider>
    )
}

interface TradingPanelInnerProps {
    tickerSymbol: string
}

function TradingPanelInner({ tickerSymbol }: TradingPanelInnerProps) {
    const {
        positionDraft,
        side,
        baseTokenAmount,
        quoteTokenAmount,
        baseTokenAmountString,
        quoteTokenAmountString,
        buyingPower,
        baseAssetSymbol,
        toggleSide,
        updateAmountBase,
        updateAmountQuote,
        handleSubmit,
        isInputReady,
        isTxLoading,
        error,
        isUsingOtcEntryPrice,
        isOrderTypeMarket,
        isOrderTypeLimit,
        isOrderTypeStop,
    } = CreateOrder.useContainer()

    const { marketPrice } = useMarket(tickerSymbol)
    const { account } = Web3WalletContainer.useContainer()
    const isInitialized = !isOrderTypeMarket || !!positionDraft

    const isInsufficientBuyingPower = buyingPower?.lt(quoteTokenAmount || 0)
    // won't disable openPosition button (isOpenPositionDisabled) if there's an error when quoting and thus buyingPower is incalculable
    // e.g. when a whale's existing position > remaining liquidity, then quoting is impossible and tx will be returned with an error
    const isBuyingPowerIncalculable = buyingPower === undefined && error instanceof UnableToQuoteError

    // HasError
    const hasError = !!error || isInsufficientBuyingPower
    const isPriceImpactError = error instanceof OverPriceLimitAfterSwapError
    const isUnableToQuoteError = error instanceof UnableToQuoteError
    const realUnableToQuoteError = isInsufficientBuyingPower && !isBuyingPowerIncalculable

    const isTradeOnPerpError = () => {
        if (hasError) {
            if (isUnableToQuoteError) {
                return realUnableToQuoteError
            } else {
                return true
            }
        }
        return false
    }

    const isTradeOnSmartMakerError = () => {
        if (hasError) {
            if (isUnableToQuoteError) {
                return realUnableToQuoteError
            }
            if (isInsufficientBuyingPower) {
                return true
            }
            return !isPriceImpactError
        }
        return false
    }

    const isMarketOrderDisabled = !positionDraft || isTxLoading || !isInputReady || isTradeOnPerpError() || !account

    const isOtcOrderDisabled = !positionDraft || isTxLoading || !isInputReady || isTradeOnSmartMakerError() || !account

    const isCreateLimitOrderDisabled = isTxLoading || !isInputReady || isTradeOnPerpError() || !account

    const isShort = side === PositionSide.SHORT

    const { t } = useTranslation()

    // NOTE: temporary solution, we should have an accurate buying power formula to handle maker+taker case
    const customizedError =
        (isInsufficientBuyingPower && new NotEnoughFreeCollateralError({} as ContractReadErrorParams<any>)) || error

    const isBaseAmountInvalid = error instanceof ReducePositionInvalidError
    const isQuoteAmountInvalid = isInsufficientBuyingPower

    const buttonSize = useBreakpointValue(["md", "md", "sm"])

    const calculatePrecision = (amount: Big | undefined, defaultPrecision: number) => {
        if (!amount) return defaultPrecision
        const nonZeroLength = getFirstNonZeroLog(amount.toNumber()) + 1
        return nonZeroLength > defaultPrecision ? nonZeroLength : defaultPrecision
    }
    const [isBaseFieldFocus, setBaseFieldFocus] = useState(false)

    const updateAmountBaseHandler = (value: string) => {
        updateAmountBase(value)
        setBaseFieldFocus(true)
    }
    const updateAmountQuoteHandler = (value: string) => {
        updateAmountQuote(value)
        setBaseFieldFocus(false)
    }

    return (
        <Flex h="100%" direction="column" align="stretch" w="100%">
            {/* Long Short Switcher */}
            <HStack w="100%" px={4}>
                <ButtonGroup px={0} pb={2} py={4} size="sm" spacing={0} top={0} width="100%" isDisabled={isTxLoading}>
                    <Button
                        width="100%"
                        h="29px"
                        colorScheme="green"
                        variant={side === PositionSide.LONG ? "solid" : "ghost"}
                        onClick={() => {
                            if (side === PositionSide.SHORT) {
                                toggleSide()
                            }
                        }}
                    >
                        {t("position.side_long")}
                    </Button>
                    <Button
                        width="100%"
                        h="29px"
                        colorScheme="red"
                        variant={side === PositionSide.SHORT ? "solid" : "ghost"}
                        onClick={() => {
                            if (side === PositionSide.LONG) {
                                toggleSide()
                            }
                        }}
                    >
                        {t("position.side_short")}
                    </Button>
                    <Spacer />
                </ButtonGroup>
            </HStack>
            {/* Trading panel */}
            <VStack
                h="1fr"
                flexGrow={1}
                flexShrink={1}
                overflow="auto"
                sx={scrollbarStyle}
                px={4}
                py={4}
                spacing={5}
                width="100%"
            >
                <OrderTypePriceInput marketPrice={marketPrice} />

                <VStack spacing={2} align="stretch" w="100%">
                    <Text variant="label">{t("general.amount")}</Text>

                    <VStack spacing={"-px"}>
                        <PerpNumberInputWithAddons
                            numberInputFieldProps={{ borderBottomRadius: 0 }}
                            precision={
                                isBaseFieldFocus
                                    ? 18
                                    : calculatePrecision(baseTokenAmount, BASE_ASSET_ADVANCED_PRECISION)
                            }
                            placeholder="Amount"
                            value={baseTokenAmountString}
                            onChange={updateAmountBaseHandler}
                            isInvalid={isBaseAmountInvalid}
                            isDisabled={!isInitialized || isTxLoading}
                            leftElement={
                                <TokenIconSymbol tokenSymbol={baseAssetSymbol} paddingLeft={2} paddingRight={1} />
                            }
                            trackingProps={{
                                eventName: EventName.POSITION_OPEN_BASE_AMOUNT_UPDATED,
                                payload: {
                                    baseTokenAmount: baseTokenAmountString,
                                    baseSymbol: baseAssetSymbol,
                                },
                            }}
                        />
                        <PerpNumberInputWithAddons
                            numberInputFieldProps={{ borderTopRadius: 0 }}
                            precision={isBaseFieldFocus ? calculatePrecision(quoteTokenAmount, USD_PRECISION) : 18}
                            placeholder="Amount"
                            onChange={updateAmountQuoteHandler}
                            isDisabled={!isInitialized || isTxLoading}
                            isInvalid={isQuoteAmountInvalid}
                            value={quoteTokenAmountString}
                            leftElement={<TokenIconSymbol tokenSymbol={"usd"} paddingLeft={2} paddingRight={1} />}
                            trackingProps={{
                                eventName: EventName.POSITION_OPEN_QUOTE_AMOUNT_UPDATED,
                                payload: { quoteTokenAmount: quoteTokenAmountString },
                            }}
                        />
                    </VStack>
                </VStack>

                <VStack spacing={2} align="stretch" w="100%">
                    <CreateOrderSlider />
                </VStack>

                <VStack spacing={2} align="stretch" w="100%">
                    <TradingSummary />
                </VStack>
            </VStack>
            <MotionVStack
                spacing={0}
                borderTopWidth="1px"
                borderTopColor="whiteAlpha.300"
                p={4}
                px={4}
                left={0}
                right={0}
                bottom={0}
                align="stretch"
            >
                {!isInitialized && (
                    <Alert fontSize={"xs"} mb={3}>
                        {/* TODO: manage this error msg programmatically */}
                        Draft Position is uninitialized.
                    </Alert>
                )}
                {!isUsingOtcEntryPrice && isTradeOnPerpError() && customizedError && (
                    <ErrorAlert error={customizedError} />
                )}
                {isUsingOtcEntryPrice && isTradeOnSmartMakerError() && customizedError && (
                    <ErrorAlert error={customizedError} />
                )}
                {isOrderTypeLimit || isOrderTypeStop ? (
                    <TradingSubmitLimitOrder isDisabled={isCreateLimitOrderDisabled} />
                ) : (
                    <>
                        <TradingSubmitButton
                            size={buttonSize}
                            isShort={isShort}
                            marketOrderDisabled={isMarketOrderDisabled}
                            otcOrderDisabled={isOtcOrderDisabled}
                            onClick={handleSubmit}
                        />
                    </>
                )}
            </MotionVStack>
        </Flex>
    )
}
